import React, { useState } from "react";
import { useStripe, useElements } from "@stripe/react-stripe-js";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import {
  useCreatesubscriptionMutation,
  useUpdatesubscriptionMutation,
} from "../../../store/Reducer/subscription";

export default function CardScreen() {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const location = useLocation();
  const { planTitle, planType, plan } = location.state || {};
  const { user } = useSelector((state) => state.Auth);
  const [createsubscription] = useCreatesubscriptionMutation();
  const [updatesubscription] = useUpdatesubscriptionMutation();

  const handleAttachCard = async () => {
    setIsLoading(true);
    setErrorMsg(null);

    if (!stripe || !elements) {
      setErrorMsg("Stripe is not properly loaded.");
      setIsLoading(false);
      return;
    }

    try {
      // Get individual Stripe Elements
      const cardNumberElement = elements.getElement(CardNumberElement);
      const cardExpiryElement = elements.getElement(CardExpiryElement);
      const cardCvcElement = elements.getElement(CardCvcElement);

      if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
        setErrorMsg("Please enter valid card details.");
        setIsLoading(false);
        return;
      }

      // Create a payment method using Stripe Elements
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement, // Uses only the cardNumberElement
      });

      if (error) {
        console.error("Stripe Error:", error);
        setErrorMsg(error.message || "An unexpected error occurred. Please try again.");
      } else {
        console.log("Payment Method Created:", paymentMethod);
        await handleSubscribe(planTitle, planType, paymentMethod.id);
      }
    } catch (error) {
      console.error("Error creating payment method:", error);
      setErrorMsg(error.message || "An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async (planTitle, planType, paymentMethodId) => {
    try {
      setIsLoading(true);

      const data = {
        userId: user?.data?._id,
        type: planType?.toLowerCase() === "website" ? "web" : "display",
        paymentMethodId,
      };

      let response;
      if (plan) {
        response = await updatesubscription({ ...data, newPlan: planTitle?.toLowerCase() }).unwrap();
      } else {
        response = await createsubscription({ ...data, plan: planTitle?.toLowerCase() }).unwrap();
      }

      toast.success(response?.message || "Subscription successful!");
    } catch (error) {
      console.error("Subscription failed:", error);
      toast.error(error?.data?.message || "An error occurred during subscription.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 p-6">
      {/* Header Section */}
      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold text-gray-900">Confirm Your Subscription</h2>
        <p className="text-gray-600 text-lg max-w-2xl mt-2">
          You have selected the <strong>{planTitle}</strong> plan for <strong>{planType}</strong>.
        </p>
        <p className="text-gray-500 mt-1">Enter your payment details below to complete your subscription.</p>
      </div>

      {/* Payment Card Section */}
      <div className="bg-white shadow-md rounded-lg p-6 max-w-md w-full">
        <h3 className="text-xl font-semibold text-gray-800 text-center mb-4">Payment Details</h3>

        {/* Card Number Field */}
        <div className="mb-4">
          <label className="block text-gray-700 font-medium mb-1">Card Number</label>
          <div className="border p-3 rounded-md">
            <CardNumberElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    "::placeholder": { color: "#aab7c4" },
                  },
                  invalid: { color: "#9e2146" },
                },
              }}
            />
          </div>
        </div>

        {/* Expiry Date and CVC Fields */}
        <div className="flex space-x-4">
          {/* Expiry Date */}
          <div className="w-1/2">
            <label className="block text-gray-700 font-medium mb-1">Expiry Date</label>
            <div className="border p-3 rounded-md">
              <CardExpiryElement
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": { color: "#aab7c4" },
                    },
                    invalid: { color: "#9e2146" },
                  },
                }}
              />
            </div>
          </div>

          {/* CVC */}
          <div className="w-1/2">
            <label className="block text-gray-700 font-medium mb-1">CVC</label>
            <div className="border p-3 rounded-md">
              <CardCvcElement
                options={{
                  style: {
                    base: {
                      fontSize: "16px",
                      color: "#424770",
                      "::placeholder": { color: "#aab7c4" },
                    },
                    invalid: { color: "#9e2146" },
                  },
                }}
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleAttachCard}
          disabled={isLoading}
          className={`w-full text-white font-medium py-3 mt-4 rounded-md transition ${
            isLoading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {isLoading ? "Processing..." : "Confirm & Subscribe"}
        </button>

        {/* Error Message */}
        {errorMsg && <p className="text-red-500 text-center mt-4">{errorMsg}</p>}
      </div>
    </div>
  );
}
